"use client";

import React, {JSX, memo, ReactNode, useRef} from "react";

import Image from "next/image";

import {useTranslation} from "@/shared/i18n";
import {ButtonSimple} from "@/shared/ui/buttons";
import {MaxLinesConfig, MaxLinesValue, SSREstimation, useExpandableText} from "@/shared/ui/data-display/expandable-text/use-expandable-text";
import {assetPath} from "@/shared/utils/asset-path";

import "./styles/expandable-text.scss";

interface ExpandableTextWithButtonProps {
    children: ReactNode;
    /**
     * Maximum lines to show when collapsed.
     * Can be a value (0, 3, 4, 6, 8, 10, 12, 14, 16) or config per device type.
     * @example maxLines={6}
     * @example maxLines={{ mobile: 4, tablet: 6, desktop: 8 }}
     */
    maxLines?: MaxLinesValue | MaxLinesConfig;
    className?: string;
    buttonClassName?: string;
    buttonSize?: "sm" | "md";
    expandText?: string;
    collapseText?: string;
    gap?: number | string;
    buttonMarginTop?: number | string;
    /** Character count of the content for SSR prediction */
    charCount?: number;
    /**
     * SSR estimation config per device type
     * @example
     * ssrEstimation={{
     *   mobile: { width: 320, fontSize: 14 },
     *   tablet: { width: 600 },
     *   desktop: { width: 800 }
     * }}
     */
    ssrEstimation?: SSREstimation;
}

export const ExpandableTextWithButton = memo(function ExpandableTextWithButton({
    children,
    maxLines,
    className = "",
    buttonClassName = "",
    buttonSize,
    expandText,
    collapseText,
    gap,
    buttonMarginTop,
    charCount,
    ssrEstimation,
}: ExpandableTextWithButtonProps): JSX.Element {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const {t} = useTranslation();
    const _expandText = expandText ?? t("common.readMore");
    const _collapseText = collapseText ?? t("common.readLess");

    const expandable = useExpandableText({
        containerRef,
        charCount: charCount ?? 0,
        maxLines,
        ssrEstimation,
    });

    return (
        <div
            className={`expandable-text-wrapper ${className}`}
            style={{
                gap: gap !== undefined ? (typeof gap === "number" ? `${gap}px` : gap) : undefined,
            }}
        >
            <div
                ref={containerRef}
                className={`expandable-text expandable-text--${expandable.maxLines}-lines ${!expandable.isReady ? "expandable-text--measuring" : ""} ${expandable.showEllipsis && expandable.canExpand ? "expandable-text--ellipsis" : ""}`}
                style={{
                    maxHeight: expandable.isReady
                        ? expandable.isExpanded
                            ? expandable.isExpandAnimationComplete
                                ? "none" // After animation - use auto height
                                : `${expandable.expandedHeight}px` // During animation - use fixed height
                            : `${expandable.collapsedHeight}px`
                        : undefined,
                }}
            >
                {children}
            </div>

            {/* Show button based on SSR prediction or client measurement */}
            {expandable.canExpand && (
                <ButtonSimple
                    className={`expandable-text__button ${buttonClassName}`}
                    variant="primary"
                    size={buttonSize}
                    onClick={expandable.toggle}
                    style={{
                        marginTop:
                            buttonMarginTop !== undefined
                                ? typeof buttonMarginTop === "number"
                                    ? `${buttonMarginTop}px`
                                    : buttonMarginTop
                                : undefined,
                    }}
                >
                    {expandable.isExpanded ? _collapseText : _expandText}
                    <span className="expandable-text__icon">
                        <span className={`expandable-text__icon-item ${expandable.isExpanded ? "expandable-text__icon-item--hidden" : ""}`}>
                            <Image src={assetPath("/images/icons/add.svg")} alt="Expand" width={16} height={16} unoptimized />
                        </span>
                        <span className={`expandable-text__icon-item ${!expandable.isExpanded ? "expandable-text__icon-item--hidden" : ""}`}>
                            <Image src={assetPath("/images/icons/remove.svg")} alt="Collapse" width={16} height={16} unoptimized />
                        </span>
                    </span>
                </ButtonSimple>
            )}
        </div>
    );
});
